"use client";

import { create } from "zustand";
import { useShoppingCartStore } from "@/lib/client/stores";
import { fetcher } from "@/lib/http/fetcher";
import type { ProductForClient } from "@/lib/shared/types/interfaces";

async function persistFavProducts(productIds: string[]) {
  const userId = useShoppingCartStore.getState().userId;
  if (!userId) return;
  try {
    await fetcher<{ ok: boolean }>("/api/users/favorites", {
      method: "PATCH",
      body: JSON.stringify({ favProductIds: productIds }),
    });
  } catch (error) {
    console.error("Failed to persist favorite products:", error);
  }
}

interface FavProductStore {
  favProducts?: ProductForClient[];
  addFavProduct: (product: ProductForClient) => void;
  removeFavProduct: (product: ProductForClient) => void;
  setFavProducts: (products: ProductForClient[]) => void;
}

/**
 * Favorite products store
 * Manages user's favorite products and persists to user database
 */
export const useFavProductStore = create<FavProductStore>((set) => ({
  favProducts: undefined,
  setFavProducts: (products: ProductForClient[]) => {
    set({ favProducts: products });
  },
  addFavProduct: (product: ProductForClient) => {
    set((state) => {
      const existingProducts = state.favProducts ?? [];
      const newFavProducts = [...existingProducts, product];
      void persistFavProducts(newFavProducts.map((p) => String(p.id)));
      return { favProducts: newFavProducts };
    });
  },
  removeFavProduct: (product: ProductForClient) => {
    set((state) => {
      const newFavProducts =
        state.favProducts?.filter((favProduct) => favProduct.id !== product.id) ?? [];
      void persistFavProducts(newFavProducts.map((p) => String(p.id)));
      return { favProducts: newFavProducts };
    });
  },
}));
